import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router';

import { Button } from '@/components/Button/Button';
import { ControlPanelSummary } from '@/features/content/control-panel/ControlPanelSummary';
import { useUpdateStats } from '@/features/content/stats/useUpdateStats';
import type { ComponentPageData } from '@/features/content/useComponent';
import { useStudentView } from '@/features/studentView/StudentViewProvider';

import { CoachDetailedScoresDialog } from './CoachDetailedScoresDialog';
import { EditCoachButton } from './KaiCoachEditControls';
import { KaiCoachEditor } from './KaiCoachEditor';
import { KaiCoachStudentView } from './KaiCoachStudentView';
import {
  coachVisibleMaxScore,
  earnedByUnit,
  isThreadOpen,
  normalizeCoachSection,
  type RawSectionStatLike,
} from './kaiCoachContent';
import { getKaiCoachSection } from './kaiCoachSection';
import type { RawKaiCoachSection } from './kaiCoachTypes';
import styles from './KaiCoachModulePage.module.css';

interface KaiCoachModulePageProps {
  canEdit: boolean;
  component: ComponentPageData;
  courseId: string;
  // Whether the component is currently released/visible to students.
  isVisibleToStudents: boolean;
  moduleId: string;
  // The authored coach section (already detected by the caller).
  section: RawKaiCoachSection;
}

function asString(value: unknown, fallback: string): string {
  return typeof value === 'string' && value ? value : fallback;
}

function formatPoints(value: number): string {
  return Number.isInteger(value) ? String(value) : String(Number(value.toFixed(2)));
}

// Pull the aggregated instructor section_stats out of the Update-Stats RPC
// response (a GetCourseStudentComponentResponse, possibly wrapped in the legacy
// { Response } envelope). Returns a unitUid → class-average map.
function classAveragesFromResponse(response: unknown): Record<string, number> {
  const root =
    response && typeof response === 'object' && 'Response' in response
      ? (response as { Response: unknown }).Response
      : response;
  const component = (root as { component?: unknown; Component?: unknown } | null)?.component ??
    (root as { Component?: unknown } | null)?.Component;
  const stats = (component as { section_stats?: unknown; sectionStats?: unknown } | null)
    ?.section_stats ??
    (component as { sectionStats?: unknown } | null)?.sectionStats;
  return earnedByUnit(Array.isArray(stats) ? (stats as RawSectionStatLike[]) : null);
}

/**
 * Page chrome for a Kai Coach module: a Course Home link, the instructor's
 * floating "Edit coach" button (edit mode is the `?edit` param, matching the
 * create flow; the editor's own floating bar leaves it), the instructor
 * visibility/release controls, and the coach editor or student view. This
 * restores the navigation/instructor affordances that the standard content
 * page provides — the bare coach views had none.
 */
export function KaiCoachModulePage({
  canEdit,
  component,
  courseId,
  isVisibleToStudents,
  moduleId,
  section,
}: KaiCoachModulePageProps) {
  const [searchParams, setSearchParams] = useSearchParams();
  const isEditing = canEdit && searchParams.get('edit') === '1';

  const normalized = useMemo(() => normalizeCoachSection(section), [section]);
  const creditEnabled = normalized.creditEnabled;
  const updateStats = useUpdateStats();
  const statsTitleId = useId();
  const [isDownloadOpen, setIsDownloadOpen] = useState(false);
  // An instructor's own record holds the class averages Update Stats stored, not
  // a score of theirs: staff earn none (issue #174).
  const ownStats = earnedByUnit(
    component.rawComponent.section_stats ?? component.rawComponent.sectionStats,
  );
  // Per-unit class averages: from the run just made, whose answer is newer than
  // the refetch can be, or else from the last run, as stored.
  const classAverages = updateStats.isSuccess
    ? classAveragesFromResponse(updateStats.data)
    : canEdit && Object.keys(ownStats).length > 0
      ? ownStats
      : null;

  // Focus follows the instructor between the modes: to the editor's heading
  // when they open it with the button (not when a link opens it), and back to
  // the button when they leave it.
  const editButtonRef = useRef<HTMLButtonElement>(null);
  const [openedWithButton, setOpenedWithButton] = useState(false);
  const returnFocusRef = useRef(false);
  useEffect(() => {
    if (!isEditing && returnFocusRef.current) {
      returnFocusRef.current = false;
      editButtonRef.current?.focus();
    }
  }, [isEditing]);

  const setEditing = (next: boolean) => {
    setSearchParams(
      (current) => {
        const params = new URLSearchParams(current);
        if (next) {
          params.set('edit', '1');
        } else {
          params.delete('edit');
        }
        return params;
      },
      { replace: true },
    );
  };

  const openEditor = () => {
    setOpenedWithButton(true);
    setEditing(true);
  };

  const exitEditor = () => {
    returnFocusRef.current = true;
    setOpenedWithButton(false);
    setEditing(false);
  };

  // Save by cloning the loaded component (so its visibility/timing/other fields
  // persist) and swapping only the coach section.
  const editorContext = {
    baseComponent: component.rawComponent,
    coachUid: asString(section.uid, ''),
    componentId: moduleId,
    courseId,
  };

  const showCoach = canEdit || isVisibleToStudents;

  // Reveal sessions students cannot reach yet. INSTRUCTOR-ONLY by construction:
  //  - canEdit is userRole === 'instructor', and student view masks the identity
  //    so it is already false there (which is why student view correctly hides
  //    these today);
  //  - studentView.active requires a masked staff test-student session, so a real
  //    student can never satisfy either branch.
  // The flag is display-only — it never reaches the participation score.
  const studentView = useStudentView();
  const revealLockedThreads =
    canEdit || (studentView.active && studentView.showHiddenContent);

  return (
    // The floating controls sit over the page's bottom-right corner; the page
    // leaves room for them so nothing at its end stays covered.
    <section className={styles.page} data-edit-controls={canEdit ? 'true' : undefined}>
      <header className={styles.bar}>
        <Link className={styles.back} to={`/app/courses/${courseId}`}>
          ← Course Home
        </Link>
      </header>

      {/* The module's name, for every viewer. It is what the instructor renames
          in the editor, and until now the page never showed it. */}
      <h1 className={styles.heading}>{component.title}</h1>

      {canEdit ? (
        <ControlPanelSummary
          component={component}
          componentId={moduleId}
          courseId={courseId}
        />
      ) : null}

      {canEdit && creditEnabled && !isEditing ? (
        <section aria-labelledby={statsTitleId} className={styles.statsPanel}>
          <div className={styles.statsHeader}>
            <div>
              <p className={styles.statsTitle} id={statsTitleId}>
                Coach stats
              </p>
              <p className={styles.statsHelp}>
                Recompute class-level averages from every student’s coach score — like
                “Update Stats” on a regular assignment.
              </p>
              {/* There before the press, so a screen reader hears the change. */}
              <p className={styles.statsStatus} role="status">
                {updateStats.isSuccess ? 'Class averages updated.' : ''}
              </p>
            </div>
            <div className={styles.statsActions}>
              <Button
                onClick={() => setIsDownloadOpen(true)}
                variant="secondary"
              >
                Download Detailed Scores
              </Button>
              <Button
                disabled={updateStats.isPending}
                onClick={() => updateStats.mutate({ coach: true, component, componentId: moduleId, courseId })}
                variant="secondary"
              >
                {updateStats.isPending ? 'Updating…' : 'Update Stats'}
              </Button>
            </div>
          </div>

          {updateStats.isError ? (
            <p className={styles.statsError} role="alert">
              Could not update stats. Please try again.
            </p>
          ) : null}

          {classAverages ? (
            <table className={styles.statsTable}>
              <thead>
                <tr>
                  <th scope="col">{normalized.sessionDimensionLabel}</th>
                  <th scope="col">Class average</th>
                </tr>
              </thead>
              <tbody>
                {normalized.threads
                  .filter((thread) => isThreadOpen(thread, Date.now()) && thread.points > 0)
                  .map((thread) => (
                    <tr key={thread.key}>
                      <td>
                        {thread.sessionLabel && thread.sessionLabel !== thread.label
                          ? `${thread.sessionLabel} · ${thread.label}`
                          : thread.label}
                      </td>
                      <td>
                        {formatPoints(classAverages[thread.scoreUnitUid] ?? 0)} /{' '}
                        {formatPoints(thread.points)}
                      </td>
                    </tr>
                  ))}
                <tr className={styles.statsTotalRow}>
                  <td>Total</td>
                  <td>
                    {formatPoints(
                      normalized.threads
                        .filter((thread) => isThreadOpen(thread, Date.now()))
                        .reduce(
                          (sum, thread) => sum + (classAverages[thread.scoreUnitUid] ?? 0),
                          0,
                        ),
                    )}{' '}
                    / {formatPoints(coachVisibleMaxScore(normalized.threads, Date.now()))}
                  </td>
                </tr>
              </tbody>
            </table>
          ) : null}

          <CoachDetailedScoresDialog
            courseId={courseId}
            moduleId={moduleId}
            moduleTitle={component.title}
            onOpenChange={setIsDownloadOpen}
            open={isDownloadOpen}
            section={section}
          />
        </section>
      ) : null}

      {!showCoach ? (
        <p className={styles.unavailable} role="status">
          This coach isn’t available yet.
        </p>
      ) : isEditing ? (
        <KaiCoachEditor
          context={editorContext}
          focusOnOpen={openedWithButton}
          onExit={exitEditor}
          section={section}
        />
      ) : (
        <KaiCoachStudentView
          courseId={courseId}
          earnedByUnit={canEdit ? {} : ownStats}
          // A link can name the thread to open on, e.g. from Due Soon.
          initialThreadKey={searchParams.get('thread')}
          moduleId={moduleId}
          revealLockedThreads={revealLockedThreads}
          section={section}
        />
      )}

      {canEdit && !isEditing ? <EditCoachButton onClick={openEditor} ref={editButtonRef} /> : null}
    </section>
  );
}
